'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

interface NavbarDropdownProps {
  isScrolled: boolean;
}

export default function NavbarDropdown({ isScrolled }: NavbarDropdownProps) {
  const t = useTranslations('navigation');
  const locale = useLocale();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dropdownItems = [
    { href: '/sports', label: t('sports') },
    { href: '/music', label: t('music') },
    { href: '/green-school', label: t('greenSchool') },
  ];

  const isActive = (href: string) => {
    return pathname.includes(href);
  };

  const hasActiveChild = () => {
    return dropdownItems.some(item => isActive(item.href));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`text-base font-medium transition-colors duration-200 flex items-center space-x-1 ${
          hasActiveChild()
            ? isScrolled
              ? 'text-white' // White when active and scrolled
              : 'text-[#bb1b1b]' // Red when active and at top
            : isScrolled
            ? 'text-white hover:text-gray-200'
            : 'text-[#bb1b1b] hover:text-[#bb1b1b]/80'
        }`}
      >
        <span>{t('gallery')}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            {dropdownItems.map((item) => (
              <Link
                key={item.href}
                href={item.href === '/' ? `/${locale}` : `/${locale}${item.href}`}
                className={`block px-4 py-3 text-base transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-[#bb1b1b] bg-red-50 font-medium'
                    : 'text-gray-700 hover:text-[#bb1b1b] hover:bg-gray-50'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
